import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Progress, Alert } from 'reactstrap'
import {
  setSelectedRateForGroupAndRecalculateTax,
  setShippingPaymentMethodAction,
  setThirdPartyBillingDataAction
} from '../../../redux/storeConfig/slices/slice.checkoutRates'
import CartItemsList from '../../Cart/CartItemsList'
import ProductionTimeBanner from '../../Cart/ProductionTimeBanner'
import { useShipperHQCheck } from '../hooks/useShipperHQCheck'
import ShippingGroup from './ShippingGroup'
import ShippingPaymentMethodSelector, { SHIPPING_PAYMENT_METHOD } from './ShippingPaymentMethodSelector'

const CartWithBanner = ({ cartItems }) => (
  <>
    <ProductionTimeBanner cartItems={cartItems} />
    <CartItemsList isCheckout />
  </>
)

const ErrorState = ({ error, cartItems }) => (
  <>
    <div className="box-shadow white-background m-0 mb-3">
      <div className="ckout-header" style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb' }}>
        <h5 className="m-0" style={{ color: '#721c24', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ErrorOutlineIcon style={{ fontSize: '24px' }} />
          Shipping Rate Error
        </h5>
      </div>
      <div className="p-3">
        <Alert color="danger" className="mb-0">
          {error.message}
        </Alert>
      </div>
    </div>
    <CartWithBanner cartItems={cartItems} />
  </>
)

const ShippingProgress = ({ progress }) => (
  <div className="p-3">
    <h5 className="text-center">Calculating Shipping Rates...</h5>
    <Progress animated value={progress.progressPercent}>
      {50}%
    </Progress>
  </div>
)

const DEFAULT_THIRD_PARTY_DATA = {
  carrierCode: '',
  accountNumber: '',
  zipCode: '',
  countryCode: 'US'
}

/**
 * ShippingMethodSelector - Centralized component for all shipping-related UI
 * Handles: ShipEngine rates, ShipperHQ, third party billing, loading states, and errors
 */
const ShippingMethodSelector = () => {
  const dispatch = useDispatch()

  // Redux selectors
  const checkoutRates = useSelector(state => state.checkoutRates)
  const cartItems = useSelector(state => state.cartItems)
  const integrations = useSelector(state => state.integrations)

  const shippingGroups = checkoutRates?.rates || []
  const selectedRatesByGroup = checkoutRates?.selectedRatesByGroup || {}
  const isCalculating = checkoutRates?.isCalculating
  const calculationProgress = checkoutRates?.calculationProgress
  const shippingError = checkoutRates?.errors?.[0]
  const isFailed = checkoutRates?.status === 'failed'
  const shippingPaymentMethod = checkoutRates?.shippingPaymentMethod || SHIPPING_PAYMENT_METHOD.STANDARD
  const thirdPartyData = checkoutRates?.thirdPartyBillingData || DEFAULT_THIRD_PARTY_DATA

  // Derived state
  const { isShipperHQ } = useShipperHQCheck(integrations)
  const isStandardShippingPaymentMethod = shippingPaymentMethod === SHIPPING_PAYMENT_METHOD.STANDARD
  const hasRates = shippingGroups.length > 0

  const handlePaymentMethodChange = useCallback(method => dispatch(setShippingPaymentMethodAction(method)), [dispatch])

  const handleThirdPartyDataChange = useCallback(data => dispatch(setThirdPartyBillingDataAction(data)), [dispatch])

  const handleRateSelect = useCallback(
    (groupId, rate, cartItemIds, customerAddressId) => {
      dispatch(
        setSelectedRateForGroupAndRecalculateTax(groupId, {
          service_code: rate.service_code,
          cost_in_cents: rate.cost_in_cents,
          encrypted_cost: rate.encrypted_cost,
          carrier_service_id: rate.carrier_service_id,
          cart_item_ids: cartItemIds,
          customer_address_id: customerAddressId
        })
      )
    },
    [dispatch]
  )

  // Error state (only show if no rates yet)
  if (isFailed && shippingError && !hasRates) {
    return <ErrorState error={shippingError} cartItems={cartItems} />
  }

  return (
    <div className="shipping-method-selector box-shadow white-background m-0 mb-3">
      <div className="ckout-header">
        <h5 className="m-0">Shipping Method</h5>
      </div>

      <ProductionTimeBanner cartItems={cartItems} />

      <div className="p-3">
        <ShippingPaymentMethodSelector
          shippingPaymentMethod={shippingPaymentMethod}
          onShippingPaymentMethodChange={handlePaymentMethodChange}
          thirdPartyData={thirdPartyData}
          onThirdPartyDataChange={handleThirdPartyDataChange}
        />

        {isStandardShippingPaymentMethod && !isShipperHQ ? (
          <>
            {isCalculating && calculationProgress && <ShippingProgress progress={calculationProgress} />}

            {shippingGroups.map((group, index) => (
              <ShippingGroup
                key={group.carrier_service_group_id}
                group={group}
                groupIndex={index}
                totalGroups={shippingGroups.length}
                selectedRate={selectedRatesByGroup[group.carrier_service_group_id]}
                onRateSelect={rate =>
                  handleRateSelect(
                    group.carrier_service_group_id,
                    rate,
                    group.line_items.map(li => li.cart_item_id),
                    group.destination_address?.id
                  )
                }
                cartItems={cartItems}
              />
            ))}
          </>
        ) : (
          <CartItemsList isCheckout />
        )}
      </div>
    </div>
  )
}

export default ShippingMethodSelector
